import { useQuery } from "@tanstack/react-query";
import { searchEntities, searchUsers } from "../../../../../api/network";
import { Avatar, Box, Button, Link, Skeleton, Typography } from "@mui/material";
import { UserInfo } from "../../../../../types";
import { colors } from "../../../../../theme/palette";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDataFormatter } from "../../../../../hooks/useDataFormatter";
import { Dispatch, SetStateAction } from "react";
import { getSearchValueRoute } from "../../../../../routers/routeUtils";

const SearchList = ({
  searchValue,
  setIsFocused,
  clearSearch,
}: {
  searchValue: string;
  setIsFocused: Dispatch<SetStateAction<boolean>>;
  clearSearch: () => void;
}) => {
  const { t } = useTranslation();
  const { data: searchDataUsersOnly, isLoading: isSearchDataUsersOnlyLoading } = useQuery({
    queryKey: ["searchDataUsersOnly", searchValue],
    queryFn: () => searchUsers(3, 0, searchValue || "", "", "", "", "", ""),
    enabled: !!searchValue,
  });

  const { data: searchDataEntitiesOnly, isLoading: isSearchDataEntitiesOnlyLoading } = useQuery({
    queryKey: ["searchDataEntitiesOnly", searchValue],
    queryFn: () => searchEntities(3, 0, searchValue, "", "", false, false, ""),
    enabled: !!searchValue,
  });

  const navigate = useNavigate();

  const handleSeeAllNavigate = () => {
    navigate(getSearchValueRoute(searchValue));
    setIsFocused(false);
    clearSearch();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          paddingBottom: "20px",
          borderBottom: "1px solid #EAECF0",
          marginBottom: "20px",
        }}
      >
        <Typography
          sx={{
            color: colors.Dark1000,
            fontFamily: "Montserrat",
            fontSize: "12px",
            fontStyle: "normal",
            fontWeight: 700,
            lineHeight: "normal",
            display: "block",
            marginBottom: "17px",
          }}
        >
          {t("pages.search.talents")}
        </Typography>
        {isSearchDataUsersOnlyLoading && (
          <Box sx={{ display: "flex", flexDirection: "column", rowGap: "20px" }}>
            {[...Array(3)].map((_, idx) => (
              <UserLoadingItem key={idx} isLast={idx === 2} />
            ))}
          </Box>
        )}

        {searchDataUsersOnly?.items?.length === 0 && !isSearchDataUsersOnlyLoading && (
          <Typography variant="body16Regular">{t("pages.search.noTalents")}</Typography>
        )}

        {searchDataUsersOnly?.items?.length > 0 && !isSearchDataUsersOnlyLoading && (
          <Box sx={{ display: "flex", flexDirection: "column", rowGap: "20px" }}>
            {searchDataUsersOnly?.items.map((user: UserInfo, idx: number) => (
              <UserItem key={user.id} user={user} isLast={idx == 2} setIsFocused={setIsFocused} clearSearch={clearSearch} />
            ))}
          </Box>
        )}
      </Box>
      <Box>
        <Typography
          sx={{
            color: colors.Dark1000,
            fontFamily: "Montserrat",
            fontSize: "12px",
            fontStyle: "normal",
            fontWeight: 700,
            lineHeight: "normal",
            display: "block",
            marginBottom: "17px",
          }}
        >
          {t("pages.search.footballEntities")}
        </Typography>
        {isSearchDataEntitiesOnlyLoading && (
          <Box sx={{ display: "flex", flexDirection: "column", rowGap: "20px" }}>
            {[...Array(3)].map((_, idx) => (
              <UserLoadingItem key={idx} isLast={idx === 2} />
            ))}
          </Box>
        )}

        {searchDataEntitiesOnly?.items?.length === 0 && !isSearchDataEntitiesOnlyLoading && (
          <Typography variant="body16Regular">{t("pages.search.noEntities")}</Typography>
        )}

        {searchDataEntitiesOnly?.items?.length > 0 && !isSearchDataEntitiesOnlyLoading && (
          <Box sx={{ display: "flex", flexDirection: "column", rowGap: "20px" }}>
            {searchDataEntitiesOnly?.items.map((user: UserInfo, idx: number) => (
              <UserItem key={user.id} user={user} isLast={idx == 2} setIsFocused={setIsFocused} clearSearch={clearSearch} />
            ))}
          </Box>
        )}

        {!isSearchDataUsersOnlyLoading &&
          !isSearchDataEntitiesOnlyLoading &&
          (searchDataEntitiesOnly?.items?.length > 0 || searchDataUsersOnly?.items?.length > 0) && (
            <Button sx={{ marginTop: "20px" }} variant="text-sm" fullWidth onClick={handleSeeAllNavigate}>
              {t("pages.search.seeAll")}
            </Button>
          )}
      </Box>
    </Box>
  );
};

export default SearchList;

const UserItem = ({
  user,
  isLast,
  setIsFocused,
  clearSearch,
}: {
  user: UserInfo;
  isLast: boolean;
  setIsFocused: Dispatch<SetStateAction<boolean>>;
  clearSearch: () => void;
}) => {
  const { fullName, id, profileLink, role, firstTwoChr, media } = useDataFormatter(user);
  const navigate = useNavigate();
  const handleNavigate = () => {
    setIsFocused(false);
    clearSearch();
    navigate(profileLink);
  };
  return (
    <Box
      key={id}
      sx={{
        display: "flex",
        alignItems: "center",
        columnGap: "12px",
        paddingBottom: !isLast ? "16px" : "",
        borderBottom: !isLast ? "1px solid #EAECF0" : "",
      }}
    >
      <Avatar
        src={media}
        sx={{
          width: "48px",
          height: "48px",
        }}
      >
        {firstTwoChr}
      </Avatar>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Link
          onClick={handleNavigate}
          sx={{
            color: colors.Dark1000,
            fontFamily: "Montserrat",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "24px",
            textDecoration: "none",
            cursor: "pointer",
          }}
        >
          {fullName}
        </Link>
        <Typography
          variant="body12Regular"
          sx={{
            color: colors.Dark600,
          }}
        >
          {role}
        </Typography>
      </Box>
    </Box>
  );
};

const UserLoadingItem = ({ isLast }: { isLast: boolean }) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        columnGap: "12px",
        paddingBottom: !isLast ? "16px" : "",
        borderBottom: !isLast ? "1px solid #EAECF0" : "",
      }}
    >
      <Skeleton variant="circular" width={48} height={48} />
      <Box sx={{ display: "flex", flexDirection: "column", rowGap: "4px" }}>
        <Skeleton variant="text" width="120px" height="24px" />
        <Skeleton variant="text" width="80px" height="20px" />
      </Box>
    </Box>
  );
};
